import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { KeyRound, Eye, EyeOff, ShieldCheck, AlertTriangle } from 'lucide-react';
import api from '../services/api';

const ForcePasswordChange = ({ open, onSuccess, userName }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('Au moins 8 caractères');
    if (!/[A-Z]/.test(password)) errors.push('Au moins une majuscule');
    if (!/[a-z]/.test(password)) errors.push('Au moins une minuscule');
    if (!/[0-9]/.test(password)) errors.push('Au moins un chiffre');
    return errors;
  };

  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, label: '', color: '' };
    const checks = [
      password.length >= 8,
      /[A-Z]/.test(password),
      /[a-z]/.test(password),
      /[0-9]/.test(password),
      /[!@#$%^&*(),.?":{}|<>]/.test(password)
    ];
    const score = checks.filter(Boolean).length;
    
    if (score <= 2) return { level: score, label: 'Faible', color: 'bg-red-500' };
    if (score <= 3) return { level: score, label: 'Moyen', color: 'bg-amber-500' };
    if (score <= 4) return { level: score, label: 'Fort', color: 'bg-emerald-500' };
    return { level: score, label: 'Très fort', color: 'bg-green-600' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validations
    const passwordErrors = validatePassword(newPassword);
    if (passwordErrors.length > 0) {
      setErrors({ password: passwordErrors });
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrors({ confirm: 'Les mots de passe ne correspondent pas' });
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/change-password', { new_password: newPassword });
      toast.success('Mot de passe modifié avec succès !');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setLoading(false);
    }
  };

  const strength = getPasswordStrength(newPassword);

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-md rounded-3xl" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-amber-100 to-orange-100">
            <KeyRound className="w-8 h-8 text-amber-600" />
          </div>
          <DialogTitle className="text-center text-xl" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Changement de mot de passe obligatoire
          </DialogTitle>
          <DialogDescription className="text-center">
            Bonjour <span className="font-semibold text-slate-900">{userName}</span> ! Pour des raisons de sécurité, vous devez changer votre mot de passe temporaire.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Nouveau mot de passe */}
          <div className="space-y-2">
            <Label htmlFor="newPassword">Nouveau mot de passe</Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="••••••••"
                className="pr-10 rounded-xl"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            
            {/* Indicateur de force */}
            {newPassword && (
              <div className="space-y-2">
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(i => (
                    <div 
                      key={i}
                      className={`h-1 flex-1 rounded-full ${i <= strength.level ? strength.color : 'bg-slate-200'}`}
                    />
                  ))}
                </div>
                <p className={`text-xs ${strength.level <= 2 ? 'text-red-600' : strength.level <= 3 ? 'text-amber-600' : 'text-emerald-600'}`}>
                  Force du mot de passe : {strength.label}
                </p>
              </div>
            )}
            
            {errors.password && (
              <div className="bg-red-50 rounded-lg p-3 text-sm">
                <p className="font-medium text-red-700 mb-1">Critères non respectés :</p>
                <ul className="text-red-600 space-y-1">
                  {errors.password.map((err, i) => (
                    <li key={i} className="flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {err}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Confirmation */}
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
            <Input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              className="rounded-xl"
              autoComplete="new-password"
            />
            {errors.confirm && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {errors.confirm}
              </p>
            )}
            {confirmPassword && newPassword === confirmPassword && (
              <p className="text-sm text-emerald-600 flex items-center gap-1">
                <ShieldCheck className="w-4 h-4" />
                Les mots de passe correspondent
              </p>
            )}
          </div>

          {/* Critères du mot de passe */}
          <div className="bg-slate-50 rounded-xl p-4 text-sm">
            <p className="font-medium text-slate-700 mb-2">Votre mot de passe doit contenir :</p>
            <ul className="grid grid-cols-2 gap-2 text-slate-600">
              <li className={`flex items-center gap-1 ${newPassword.length >= 8 ? 'text-emerald-600' : ''}`}>
                {newPassword.length >= 8 ? '✓' : '○'} 8 caractères min.
              </li>
              <li className={`flex items-center gap-1 ${/[A-Z]/.test(newPassword) ? 'text-emerald-600' : ''}`}>
                {/[A-Z]/.test(newPassword) ? '✓' : '○'} Une majuscule
              </li>
              <li className={`flex items-center gap-1 ${/[a-z]/.test(newPassword) ? 'text-emerald-600' : ''}`}>
                {/[a-z]/.test(newPassword) ? '✓' : '○'} Une minuscule
              </li>
              <li className={`flex items-center gap-1 ${/[0-9]/.test(newPassword) ? 'text-emerald-600' : ''}`}>
                {/[0-9]/.test(newPassword) ? '✓' : '○'} Un chiffre
              </li>
            </ul>
          </div>

          <DialogFooter>
            <Button
              type="submit"
              disabled={loading || !newPassword || !confirmPassword}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 rounded-xl py-6"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Modification...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5" />
                  Confirmer le nouveau mot de passe
                </span>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ForcePasswordChange;
